import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useInventory } from '../../contexts/InventoryContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useReservations } from '../../hooks/useReservations';
import { useCustomers } from '../../hooks/useCustomers';
import { usePOS } from '../../hooks/usePOS';
import { useB2BBuyers } from '../../hooks/useB2BBuyers';
import { Button } from '../ui/Button';
import { BarcodeScanner } from '../ui/BarcodeScanner';
import { ExternalScanner } from '../ui/ExternalScanner';
import { sendInvoiceEmail } from '../../lib/email';
import { createManualProFormaInvoice } from '../../lib/proformaInvoice';
import { sendEdgeEmail } from '../../lib/b2bEmail';
import {
  generateProFormaInvoiceHTML,
  htmlToPdfBase64,
  buildEpcQrDataUrl,
  type SupplierProfile,
} from '../../lib/pdfGenerator';
import {
  ensureDefaultLocations,
  fetchLocationQuantitiesForProduct,
  fetchLocationQuantitiesForProducts,
  resolveWarehouseLocation,
  saveLocationQuantities,
  type LocationQuantity
} from '../../utils/locationInventory';
import {
  buildProductNameGroups,
  filterProductGroups,
  type ProductNameGroup
} from '../../utils/productGrouping';
import { getAdminId } from '../../utils/getAdminId';
import { classifyScanValue } from '../../utils/scanClassify';
import { resolveWedgeScan } from '../../utils/resolveWedgeScan';
import { InhouseCommandToolbar } from './inhouse-command-toolbar';
import { InhouseOrderPanel } from './inhouse-order-panel';
import { ProductGroupCard, type ProductGroupDensity } from './product-group-card';
import { ProductVariantCard } from './product-variant-card';
import {
  ProductVariantMatrixModal,
  type VariantAddSelection
} from './product-variant-matrix-modal';
import { EmptyState } from './ui/empty-state';
import { ProductGroupCardSkeleton, ProductGroupListSkeleton } from './ui/skeleton';
import { SegmentedControl } from './ui/segmented-control';
import type { ShipmentMode } from './ui/mode-theme';
import {
  LayoutGrid,
  List,
  Package,
  X,
  Receipt as ReceiptIcon,
  CheckCircle,
  AlertTriangle,
  CreditCard
} from 'lucide-react';
import {
  CartItem,
  CustomerData,
  Reservation,
  WorkerInfo,
  LocationInfo,
  type InventoryItem,
} from '../../types';
import type { B2BBuyer } from '../../types/b2b';
import { PaymentMethod } from '../../types/pos';
import { Receipt } from '../receipt/Receipt';
import { useInvoices } from '../../hooks/useInvoices';
import { supabase } from '../../lib/supabase';
import { ReservationConfirmation } from './ReservationConfirmation';
import { TransferConfirmationModal } from './TransferConfirmationModal';
import { TransferReceiptModal } from './TransferReceiptModal';
import { ReturnStockModal } from './ReturnStockModal';
import { EnhancedPickupModal } from '../pos';
import { useProductAttributes } from '../../hooks/use-product-attributes';

interface ScanNotification {
  id: string;
  type: 'success' | 'error' | 'warning';
  itemName: string;
  price?: number;
  quantity?: number;
  message?: string;
}

export const InHouseShipmentComponent: React.FC = () => {
  const { user } = useAuth();
  const { allItems, allItemsLoading, fetchAllItems, updateItem, invalidateAllItemsCache } = useInventory();
  const { t, language } = useLanguage();
  const { createInvoice } = useInvoices();
  const {
    reservations,
    reservedQuantities,
    createReservation,
    fulfillReservation,
    getActiveReservations,
    getReservedQuantityForProduct,
    createInternalTransfer,
    confirmTransferReceipt,
    returnToWarehouse,
    getPendingTransfers,
    getCustomerReservations,
    getInternalTransfers
  } = useReservations();
  const {
    customers,
    createCustomer,
    getFavoriteCustomers,
    getRecentCustomers
  } = useCustomers();
  const {
    transactions,
    heldCarts,
    todayStats,
    createTransaction,
    holdCart,
    restoreCart,
    deleteHeldCart,
    getRecentTransactions
  } = usePOS();
  const { buyers } = useB2BBuyers();

  const [shipmentMode, setShipmentMode] = useState<ShipmentMode>('pickup');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [showExternalScanner, setShowExternalScanner] = useState(false);
  const scanBufferRef = useRef('');
  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScanTimeRef = useRef(0);
  const lastScanValueRef = useRef('');
  const handleProductScanRef = useRef<
    ((raw: string, resolvedItem?: InventoryItem) => Promise<void>) | null
  >(null);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [customerData, setCustomerData] = useState<CustomerData>({
    name: '',
    address: '',
    vatNumber: '',
    email: ''
  });
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [workerName, setWorkerName] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [scanNotifications, setScanNotifications] = useState<ScanNotification[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterColor, setFilterColor] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [showOutOfStock, setShowOutOfStock] = useState(() =>
    localStorage.getItem('inhouse-show-out-of-stock') === 'true'
  );
  const [browseDensity, setBrowseDensity] = useState<ProductGroupDensity>(() =>
    (localStorage.getItem('inhouse-browse-density') as ProductGroupDensity) || 'list'
  );
  const [browseView, setBrowseView] = useState<'grouped' | 'all'>(() =>
    (localStorage.getItem('inhouse-browse-view') as 'grouped' | 'all') || 'grouped'
  );
  const [selectedProductGroup, setSelectedProductGroup] = useState<ProductNameGroup | null>(null);
  const [resolvedWarehouseLocation, setResolvedWarehouseLocation] = useState<{ id: string; name: string } | null>(null);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  const [customTaxRate, setCustomTaxRate] = useState<number | null>(null);
  const [isEditingTax, setIsEditingTax] = useState(false);
  const [editingTaxValue, setEditingTaxValue] = useState('');
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [showReservationConfirmation, setShowReservationConfirmation] = useState(false);
  const [lastCreatedReservation, setLastCreatedReservation] = useState<Reservation | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Array<{id: string; name: string}>>([]);
  const [cartItemLocations, setCartItemLocations] = useState<Record<string, string>>({});
  const [productLocationQtys, setProductLocationQtys] = useState<Record<string, LocationQuantity[]>>({});
  const [showTransferConfirmation, setShowTransferConfirmation] = useState(false);
  const [lastCreatedTransfer, setLastCreatedTransfer] = useState<Reservation | null>(null);
  const [showTransferReceiptModal, setShowTransferReceiptModal] = useState(false);
  const [showReturnStockModal, setShowReturnStockModal] = useState(false);
  const [showTransferHistory, setShowTransferHistory] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<WorkerInfo | null>(null);
  const [transferSourceLocation, setTransferSourceLocation] = useState<string | null>(null);
  const [transferDestLocation, setTransferDestLocation] = useState<string | null>(null);
  const [transferNotes, setTransferNotes] = useState('');
  const [selectedInvoiceBuyer, setSelectedInvoiceBuyer] = useState<B2BBuyer | null>(null);
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [removeStockOnInvoice, setRemoveStockOnInvoice] = useState(false);
  const [sendInvoiceEmailFlag, setSendInvoiceEmailFlag] = useState(false);

  const adminId = getAdminId(user);
  const { colors, sizes } = useProductAttributes(adminId);
  const isOldInvoice = shipmentMode === 'old_invoice';
  const ignoreStock = isOldInvoice;
  const invoiceDiscount = selectedInvoiceBuyer?.account_discount || 1;
  const effectiveTaxRate = customTaxRate !== null ? customTaxRate : (user?.tax_percentage || 21);
  const invoiceVatPct = selectedInvoiceBuyer?.vat_percentage ?? effectiveTaxRate;

  const shouldShowPricing =
    (shipmentMode === 'pickup' && paymentMethod !== 'worker_pickup') || isOldInvoice;

  const warehouseLocation = resolvedWarehouseLocation ?? availableLocations.find(l =>
    l.name.toLowerCase().includes('warehouse') || l.name.toLowerCase().includes('wearhouse')
  );
  const shopLocation = availableLocations.find(l =>
    l.name.toLowerCase().includes('shop') || l.name.toLowerCase().includes('store')
  );

  useEffect(() => {
    if (adminId) {
      fetchLocations();
      fetchAllItems();
      resolveWarehouseLocation(adminId).then(setResolvedWarehouseLocation);
    }
  }, [adminId, fetchAllItems]);

  useEffect(() => {
    localStorage.setItem('inhouse-show-out-of-stock', String(showOutOfStock));
  }, [showOutOfStock]);

  useEffect(() => {
    localStorage.setItem('inhouse-browse-density', browseDensity);
  }, [browseDensity]);

  useEffect(() => {
    localStorage.setItem('inhouse-browse-view', browseView);
  }, [browseView]);

  const fetchLocations = async () => {
    if (!adminId) return;
    const locs = await ensureDefaultLocations(adminId);
    setAvailableLocations(locs);
  };

  const loadProductLocationQtys = useCallback(async (productId: string) => {
    if (!adminId || productLocationQtys[productId]) return;
    const qtys = await fetchLocationQuantitiesForProduct(productId, adminId);
    setProductLocationQtys(prev => ({ ...prev, [productId]: qtys }));
  }, [adminId, productLocationQtys]);

  const loadAllProductLocationQtys = useCallback(async () => {
    if (!adminId || allItems.length === 0) return;
    const productIds = allItems.map(item => item.id);
    const qtysMap = await fetchLocationQuantitiesForProducts(productIds, adminId);
    const result: Record<string, LocationQuantity[]> = {};
    qtysMap.forEach((qtys, productId) => {
      result[productId] = qtys;
    });
    setProductLocationQtys(result);
  }, [adminId, allItems]);

  useEffect(() => {
    if (adminId && allItems.length > 0 && availableLocations.length > 0) {
      loadAllProductLocationQtys();
    }
  }, [adminId, allItems.length, availableLocations.length, loadAllProductLocationQtys]);

  const addScanNotification = useCallback((notification: Omit<ScanNotification, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setScanNotifications(prev => [...prev, { ...notification, id }]);

    setTimeout(() => {
      setScanNotifications(prev => prev.filter(n => n.id !== id));
    }, 2500);
  }, []);

  const removeScanNotification = useCallback((id: string) => {
    setScanNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const hasPricingAccess = user?.pricing_enabled || user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'worker';

  if (!hasPricingAccess) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-center">
            <CreditCard className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-300">
                Pricing System Required
              </h3>
              <p className="text-yellow-600 dark:text-yellow-400 mt-1">
                The in-house shipment terminal requires pricing access. Please contact your administrator to enable pricing features.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getWarehouseQuantity = (productId: string): number => {
    if (!warehouseLocation) return 0;
    const locQtys = productLocationQtys[productId];
    if (!locQtys) return 0;
    const wh = locQtys.find(q => q.locationId === warehouseLocation.id);
    return wh?.quantity || 0;
  };

  const getActiveLocationId = useCallback((): string | null => {
    if (shipmentMode === 'transfer' && transferSourceLocation) return transferSourceLocation;
    return warehouseLocation?.id ?? null;
  }, [shipmentMode, transferSourceLocation, warehouseLocation?.id]);

  const getLocationQuantity = useCallback((productId: string): number => {
    const locationId = getActiveLocationId();
    if (!locationId) return 0;
    const locQtys = productLocationQtys[productId];
    if (!locQtys) return 0;
    const match = locQtys.find(q => q.locationId === locationId);
    return match?.quantity ?? 0;
  }, [getActiveLocationId, productLocationQtys]);

  const hasLocationRecord = useCallback((productId: string): boolean => {
    const locationId = getActiveLocationId();
    if (!locationId) return false;
    const locQtys = productLocationQtys[productId];
    return !!locQtys?.some(q => q.locationId === locationId);
  }, [getActiveLocationId, productLocationQtys]);

  const getAvailableQuantity = (item: any) => {
    const reserved = getReservedQuantityForProduct(item.id);
    const locationQty = getLocationQuantity(item.id);
    return Math.max(0, locationQty - reserved);
  };

  const getCartQuantityForProduct = useCallback((productId: string): number => {
    return cart.find(c => c.id === productId)?.cartQuantity ?? 0;
  }, [cart]);

  const addToCart = (item: any, quantityToAdd = 1) => {
    if (quantityToAdd <= 0) return;

    if (shouldShowPricing && !item.sell_price) {
      alert(t('payment.itemNeedsPrice'));
      return;
    }

    const availableQty = getAvailableQuantity(item);
    const existingInCart = cart.find(c => c.id === item.id);
    const currentInCart = existingInCart?.cartQuantity ?? 0;
    const newTotal = currentInCart + quantityToAdd;

    if (newTotal > availableQty && !ignoreStock) {
      alert(shipmentMode === 'reserve'
        ? 'Not enough available stock (some items are reserved)'
        : t('payment.notEnoughStock')
      );
      return;
    }

    const existing = cart.find(cartItem => cartItem.id === item.id);

    if (existing) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? {
              ...cartItem,
              cartQuantity: newTotal,
              lineTotal: newTotal * (cartItem.sell_price || 0)
            }
          : cartItem
      ));
      return;
    }

    loadProductLocationQtys(item.id);

    const nextLocations = { ...cartItemLocations };
    if (shipmentMode === 'reserve' && warehouseLocation) {
      nextLocations[item.id] = warehouseLocation.id;
    } else if (!nextLocations[item.id] && availableLocations.length > 0) {
      const shopLoc = availableLocations.find(l => l.name.toLowerCase() === 'shop');
      nextLocations[item.id] = (shopLoc || availableLocations[0]).id;
    }
    setCartItemLocations(nextLocations);

    setCart([...cart, {
      ...item,
      unit_price: item.unit_price ?? item.sell_price ?? 0,
      cartQuantity: quantityToAdd,
      lineTotal: (item.sell_price || 0) * quantityToAdd
    } as CartItem]);
  };

  const addVariantsToCart = useCallback((selections: VariantAddSelection[]) => {
    if (selections.length === 0) return;

    let nextCart = [...cart];
    const nextLocations = { ...cartItemLocations };
    let rejected = false;
    const loadedProductIds = new Set<string>();

    for (const selection of selections) {
      const item = allItems.find(i => i.id === selection.productId);
      if (!item) continue;

      if (shouldShowPricing && !item.sell_price) {
        rejected = true;
        continue;
      }

      const reserved = getReservedQuantityForProduct(item.id);
      const locationQty = getLocationQuantity(item.id);
      const availableQty = Math.max(0, locationQty - reserved);
      const existing = nextCart.find(c => c.id === item.id);
      const currentInCart = existing?.cartQuantity ?? 0;
      const newTotal = currentInCart + selection.quantity;

      if (newTotal > availableQty && !ignoreStock) {
        rejected = true;
        continue;
      }

      if (existing) {
        nextCart = nextCart.map(c =>
          c.id === item.id
            ? {
                ...c,
                cartQuantity: newTotal,
                lineTotal: newTotal * (c.sell_price || 0)
              }
            : c
        );
      } else {
        if (shipmentMode === 'reserve' && warehouseLocation) {
          nextLocations[item.id] = warehouseLocation.id;
        } else if (!nextLocations[item.id] && availableLocations.length > 0) {
          const shopLoc = availableLocations.find(l => l.name.toLowerCase() === 'shop');
          nextLocations[item.id] = (shopLoc || availableLocations[0]).id;
        }
        nextCart.push({
          ...item,
          unit_price: item.unit_price ?? item.sell_price ?? 0,
          cartQuantity: selection.quantity,
          lineTotal: (item.sell_price || 0) * selection.quantity
        } as CartItem);
      }

      loadedProductIds.add(selection.productId);
    }

    setCart(nextCart);
    setCartItemLocations(nextLocations);

    if (rejected) {
      alert(shipmentMode === 'reserve'
        ? 'Not enough available stock (some items are reserved)'
        : t('payment.notEnoughStock')
      );
    }

    loadedProductIds.forEach(productId => loadProductLocationQtys(productId));
  }, [
    cart,
    allItems,
    shouldShowPricing,
    shipmentMode,
    warehouseLocation,
    availableLocations,
    cartItemLocations,
    getLocationQuantity,
    getReservedQuantityForProduct,
    t,
    ignoreStock
  ]);

  const updateCartQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    const item = allItems.find(i => i.id === itemId);
    const availableQty = item ? getAvailableQuantity(item) : 0;

    if (item && newQuantity > availableQty && !ignoreStock) {
      alert(shipmentMode === 'reserve'
        ? 'Not enough available stock (some items are reserved)'
        : t('payment.exceedsInventory')
      );
      return;
    }

    setCart(prev => prev.map(cartItem =>
      cartItem.id === itemId
        ? {
            ...cartItem,
            cartQuantity: newQuantity,
            lineTotal: newQuantity * (cartItem.sell_price || 0)
          }
        : cartItem
    ));
  };

  const removeFromCart = (itemId: string) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    setCustomerData({ name: '', address: '', vatNumber: '', email: '' });
    setShowCustomerForm(false);
    setCustomTaxRate(null);
    setEditingPriceId(null);
    setIsEditingTax(false);
    setWorkerName('');
    setSelectedCustomer(null);
    setCartItemLocations({});
    setSelectedWorker(null);
    setTransferNotes('');
    setSelectedInvoiceBuyer(null);
    setRemoveStockOnInvoice(false);
    setSendInvoiceEmailFlag(false);
  };

  useEffect(() => {
    if (shipmentMode === 'transfer' && availableLocations.length > 0) {
      const warehouseLoc = availableLocations.find(l =>
        l.name.toLowerCase().includes('warehouse') || l.name.toLowerCase().includes('wearhouse')
      );
      const shopLoc = availableLocations.find(l =>
        l.name.toLowerCase().includes('shop') || l.name.toLowerCase().includes('store')
      );
      if (warehouseLoc && !transferSourceLocation) {
        setTransferSourceLocation(warehouseLoc.id);
      }
      if (shopLoc && !transferDestLocation) {
        setTransferDestLocation(shopLoc.id);
      }
    }
  }, [shipmentMode, availableLocations]);

  const processTransfer = async () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    if (!selectedWorker) {
      alert('Please select a worker');
      return;
    }

    if (!transferSourceLocation || !transferDestLocation) {
      alert('Please select source and destination locations');
      return;
    }

    if (transferSourceLocation === transferDestLocation) {
      alert('Source and destination cannot be the same');
      return;
    }

    setProcessing(true);

    try {
      const transferData = {
        worker_id: selectedWorker.id,
        worker_name: selectedWorker.full_name,
        source_location_id: transferSourceLocation,
        destination_location_id: transferDestLocation,
        notes: transferNotes || undefined,
        items: cart.map(cartItem => ({
          product_id: cartItem.id,
          quantity: cartItem.cartQuantity,
          unit_price: cartItem.sell_price
        }))
      };

      const transfer = await createInternalTransfer(transferData);

      if (transfer) {
        setLastCreatedTransfer(transfer);
        setShowTransferConfirmation(true);
        clearCart();
        await loadAllProductLocationQtys();
      } else {
        alert('Failed to create transfer');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      alert('Failed to create transfer');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmTransferReceipt = async (
    transferId: string,
    receivedItems: Array<{ item_id: string; product_id: string; received_quantity: number }>,
    receivingWorkerId: string
  ): Promise<boolean> => {
    setProcessing(true);
    try {
      const success = await confirmTransferReceipt(transferId, receivedItems, receivingWorkerId);
      if (success) {
        invalidateAllItemsCache();
        await fetchAllItems(true);
        await loadAllProductLocationQtys();
        alert('Transfer received successfully! Inventory has been updated.');
      } else {
        alert('Failed to confirm transfer receipt. Please check inventory availability and try again.');
      }
      return success;
    } catch (err) {
      console.error('Transfer receipt error:', err);
      alert('An error occurred while confirming transfer receipt.');
      return false;
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateReturn = async (
    items: Array<{ product_id: string; quantity: number; source_location_id: string; destination_location_id: string }>,
    workerId: string,
    workerName: string,
    notes?: string
  ) => {
    const result = await returnToWarehouse(items, workerId, workerName, notes);
    if (result) {
      setLastCreatedTransfer(result);
      setShowTransferConfirmation(true);
      await loadAllProductLocationQtys();
    }
    return result;
  };

  const handleHoldCart = async () => {
    if (cart.length === 0) return;

    const held = await holdCart(
      cart,
      customerData.name || undefined,
      selectedCustomer?.id,
      undefined,
      effectiveTaxRate
    );

    if (held) {
      clearCart();
    }
  };

  const handleRestoreCart = (heldCart: any) => {
    const restoredItems = restoreCart(heldCart);
    setCart(restoredItems);
    if (heldCart.customer_name) {
      setCustomerData(prev => ({ ...prev, name: heldCart.customer_name }));
    }
    deleteHeldCart(heldCart.id);
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.lineTotal, 0);
    const taxRate = effectiveTaxRate / 100;
    const taxAmount = subtotal * taxRate;
    const total = subtotal + taxAmount;

    return { subtotal, taxAmount, total, taxRate };
  };

  const invoiceCart = useMemo(() => {
    if (!isOldInvoice) return cart;
    return cart.map(c => {
      const unit = (c.sell_price || 0) / invoiceDiscount;
      return {
        ...c,
        sell_price: unit,
        lineTotal: unit * c.cartQuantity,
      };
    });
  }, [cart, isOldInvoice, invoiceDiscount]);

  const calculateInvoiceTotals = useCallback(() => {
    const subtotal = invoiceCart.reduce((sum, item) => sum + item.lineTotal, 0);
    const taxAmount = subtotal * (invoiceVatPct / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  }, [invoiceCart, invoiceVatPct]);

  const displayTotals = useMemo(() => {
    if (isOldInvoice) return calculateInvoiceTotals();
    return calculateTotals();
  }, [isOldInvoice, cart, invoiceCart, invoiceVatPct, effectiveTaxRate, calculateInvoiceTotals]);

  const { subtotal, taxAmount, total } = displayTotals;

  const updateCartItemPrice = (itemId: string, newPrice: number) => {
    if (newPrice < 0) return;
    setCart(prev => prev.map(cartItem =>
      cartItem.id === itemId
        ? {
            ...cartItem,
            sell_price: newPrice,
            lineTotal: cartItem.cartQuantity * newPrice,
            priceModified: newPrice !== allItems.find(i => i.id === itemId)?.sell_price
          }
        : cartItem
    ));
  };

  const resetCartItemPrice = (itemId: string) => {
    const originalItem = allItems.find(i => i.id === itemId);
    if (originalItem?.sell_price) {
      setCart(prev => prev.map(cartItem =>
        cartItem.id === itemId
          ? {
              ...cartItem,
              sell_price: originalItem.sell_price,
              lineTotal: cartItem.cartQuantity * originalItem.sell_price!,
              priceModified: false
            }
          : cartItem
      ));
    }
  };

  const handlePriceEdit = (itemId: string, currentPrice: number) => {
    setEditingPriceId(itemId);
    setEditingPriceValue(currentPrice.toFixed(2));
  };

  const savePriceEdit = (itemId: string) => {
    const newPrice = parseFloat(editingPriceValue);
    if (!isNaN(newPrice) && newPrice >= 0) {
      updateCartItemPrice(itemId, newPrice);
    }
    setEditingPriceId(null);
    setEditingPriceValue('');
  };

  const cancelPriceEdit = () => {
    setEditingPriceId(null);
    setEditingPriceValue('');
  };

  const handleTaxEdit = () => {
    setIsEditingTax(true);
    setEditingTaxValue(effectiveTaxRate.toString());
  };

  const saveTaxEdit = () => {
    const newTax = parseFloat(editingTaxValue);
    if (!isNaN(newTax) && newTax >= 0 && newTax <= 100) {
      setCustomTaxRate(newTax);
    }
    setIsEditingTax(false);
    setEditingTaxValue('');
  };

  const resetTaxRate = () => {
    setCustomTaxRate(null);
    setIsEditingTax(false);
  };

  const findItemByCode = useCallback(
    (code: string) => {
      const normalized = code.trim().toLowerCase();
      if (!normalized) return undefined;

      const exact = allItems.find(
        (i) =>
          i.batch?.toLowerCase() === normalized ||
          i.barcode?.toLowerCase() === normalized ||
          i.sku?.toLowerCase() === normalized
      );
      if (exact) return exact;

      return allItems.find((i) => i.name.toLowerCase().includes(normalized));
    },
    [allItems]
  );

  const addResolvedItemToCart = useCallback(
    (item: InventoryItem, displayName?: string) => {
      if (shouldShowPricing && !item.sell_price) {
        addScanNotification({
          type: 'warning',
          itemName: item.name,
          message: 'No price set',
        });
        return;
      }

      const availableQty = getAvailableQuantity(item);
      const existingInCart = cart.find((c) => c.id === item.id);

      if (existingInCart && existingInCart.cartQuantity >= availableQty && !ignoreStock) {
        addScanNotification({
          type: 'warning',
          itemName: item.name,
          message: 'Max available stock reached',
        });
        return;
      }

      addToCart(item);
      addScanNotification({
        type: 'success',
        itemName: displayName || item.name,
        price: item.sell_price,
        quantity: (existingInCart?.cartQuantity || 0) + 1,
      });
    },
    [
      addScanNotification,
      addToCart,
      cart,
      getAvailableQuantity,
      ignoreStock,
      shouldShowPricing,
    ]
  );

  const handleProductScan = useCallback(
    async (raw: string, resolvedItem?: InventoryItem) => {
      const value = raw.trim();
      if (!value && !resolvedItem) return;

      if (resolvedItem) {
        const fromCatalog = allItems.find((i) => i.id === resolvedItem.id) || resolvedItem;
        addResolvedItemToCart(fromCatalog);
        return;
      }

      const result = await resolveWedgeScan(adminId, value);

      if (result.kind === 'unknown' || result.kind === 'epc_error') {
        addScanNotification({
          type: 'error',
          itemName: result.value || value,
          message: result.message,
        });
        return;
      }

      if (result.kind === 'epc') {
        if (result.integrityAlarm) {
          addScanNotification({
            type: 'error',
            itemName: result.item.name,
            message: result.message || 'DATA INTEGRITY ALARM — unit record and EPC disagree',
          });
          return;
        }

        const fromCatalog = allItems.find((i) => i.id === result.item.id) || result.item;
        const note =
          !result.unitFound && result.message
            ? ` · ${result.message}`
            : !result.unitFound
              ? ' · no unit record — matched from EPC base'
              : '';
        addResolvedItemToCart(fromCatalog, `${fromCatalog.name}${note}`);
        return;
      }

      const item = findItemByCode(result.value);
      if (!item) {
        addScanNotification({
          type: 'error',
          itemName: result.value,
          message: 'Item not found',
        });
        return;
      }

      addResolvedItemToCart(item);
    },
    [addResolvedItemToCart, addScanNotification, adminId, allItems, findItemByCode]
  );

  handleProductScanRef.current = handleProductScan;

  useEffect(() => {
    if (showCameraScanner || showExternalScanner) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }

      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);

      const now = Date.now();

      if (e.key === 'Enter') {
        e.preventDefault();
        const currentBuffer = scanBufferRef.current;
        if (currentBuffer.length === 0) return;

        const kind = classifyScanValue(currentBuffer);
        const dedupeMs = kind === 'epc' ? 1200 : 150;
        const isDuplicate =
          now - lastScanTimeRef.current < dedupeMs &&
          currentBuffer === lastScanValueRef.current;

        if (!isDuplicate) {
          lastScanTimeRef.current = now;
          lastScanValueRef.current = currentBuffer;
          setTimeout(() => {
            void handleProductScanRef.current?.(currentBuffer);
          }, 50);
        }
        scanBufferRef.current = '';
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        scanBufferRef.current += e.key;
        scanTimeoutRef.current = setTimeout(() => {
          scanBufferRef.current = '';
        }, 300);
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
    };
  }, [showCameraScanner, showExternalScanner]);

  const processReservation = async () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    if (!selectedWorker) {
      alert('Please select a worker');
      return;
    }

    setProcessing(true);

    try {
      const reservationData = {
        customer_name: selectedWorker.full_name,
        customer_email: undefined,
        customer_phone: undefined,
        customer_address: undefined,
        notes: `Internal reservation by ${selectedWorker.full_name}`,
        items: cart.map(cartItem => ({
          product_id: cartItem.id,
          quantity: cartItem.cartQuantity,
          unit_price: 0,
          total_price: 0,
          source_location_id: warehouseLocation?.id || cartItemLocations[cartItem.id] || undefined
        })),
        subtotal: 0,
        tax_amount: 0,
        total_amount: 0
      };

      const reservation = await createReservation(reservationData);

      if (reservation) {
        setLastCreatedReservation(reservation);
        setShowReservationConfirmation(true);
        clearCart();
      } else {
        alert('Failed to create reservation');
      }
    } catch (error) {
      console.error('Reservation error:', error);
      alert('Failed to create reservation');
    } finally {
      setProcessing(false);
    }
  };

  const processPickup = async () => {
    if (cart.length === 0) {
      alert(t('payment.cartEmpty'));
      return;
    }

    if (!customerData.name.trim()) {
      alert(t('payment.enterCustomerName'));
      return;
    }

    if (paymentMethod === 'worker_pickup' && !workerName.trim()) {
      alert('Please enter worker name for internal pickup');
      return;
    }

    setProcessing(true);

    try {
      const { subtotal, taxAmount, total } = calculateTotals();
      const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const paymentStatus = paymentMethod === 'loan' ? 'unpaid' : paymentMethod === 'worker_pickup' ? 'internal' : 'paid';

      const invoiceData = {
        user_id: user?.id || '',
        invoice_number: `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
        invoice_date: new Date().toISOString(),
        due_date: new Date(Date.now() + (user?.payment_terms_days || 14) * 24 * 60 * 60 * 1000).toISOString(),
        customer_name: customerData.name.trim(),
        customer_address: customerData.address?.trim() || '',
        customer_vat_number: customerData.vatNumber?.trim() || '',
        customer_email: customerData.email?.trim() || '',
        subtotal_amount: subtotal,
        tax_amount: taxAmount,
        total_amount: total,
        payment_method: (paymentMethod === 'worker_pickup' ? 'cash' : paymentMethod === 'loan' ? 'lowan' : paymentMethod) as 'cash' | 'pin' | 'bank_transfer' | 'lowan',
        payment_status: paymentStatus as 'paid' | 'unpaid',
        currency: 'EUR',
        language: language as 'en' | 'nl',
        notes: `In-house Shipment - ${transactionId}`,
        invoice_items: cart.map(cartItem => ({
          id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          invoice_id: '',
          inventory_item_id: cartItem.id,
          description: cartItem.name,
          quantity: cartItem.cartQuantity,
          unit_price: cartItem.sell_price!,
          total_price: cartItem.lineTotal,
          vat_rate: effectiveTaxRate,
          vat_amount: cartItem.lineTotal * (effectiveTaxRate / 100),
          created_at: new Date().toISOString()
        })) as any
      };

      const savedInvoice = await createInvoice(invoiceData);

      if (!savedInvoice) {
        throw new Error('Failed to save invoice to database');
      }

      for (const cartItem of cart) {
        const whQty = getWarehouseQuantity(cartItem.id);
        const newWhQty = Math.max(0, whQty - cartItem.cartQuantity);
        const newTotalQty = Math.max(0, cartItem.quantity - cartItem.cartQuantity);

        const success = await updateItem(cartItem.id, {
          quantity: newTotalQty
        });

        if (!success) {
          throw new Error(`Failed to update inventory for ${cartItem.name}`);
        }

        if (warehouseLocation && adminId) {
          const locQtys = productLocationQtys[cartItem.id] || [];
          const updatedQtys = locQtys.map(lq => ({
            locationId: lq.locationId,
            quantity: lq.locationId === warehouseLocation.id ? newWhQty : lq.quantity
          }));
          await saveLocationQuantities(cartItem.id, adminId, updatedQtys);
        }
      }

      if (customerData.email?.trim()) {
        try {
          await sendInvoiceEmail(
            savedInvoice,
            user!,
            customerData.email.trim()
          );
        } catch (emailError) {
          console.warn('Failed to send email:', emailError);
        }
      }

      await createTransaction({
        customer_id: selectedCustomer?.id,
        customer_name: customerData.name.trim(),
        payment_method: paymentMethod,
        payment_status: paymentMethod === 'loan' ? 'unpaid' : paymentMethod === 'worker_pickup' ? 'internal' : 'paid',
        worker_id: paymentMethod === 'worker_pickup' ? user?.id : undefined,
        worker_name: paymentMethod === 'worker_pickup' ? workerName.trim() : undefined,
        subtotal,
        tax_amount: taxAmount,
        total_amount: total,
        items_count: cart.length,
        invoice_id: savedInvoice.id,
        notes: paymentMethod === 'worker_pickup' ? `Worker pickup by ${workerName}` : undefined
      });

      const getPaymentMethodLabel = () => {
        switch (paymentMethod) {
          case 'cash': return 'Cash';
          case 'pin': return 'Card/PIN';
          case 'loan': return 'Invoice (On Credit)';
          case 'worker_pickup': return `Worker Pickup (${workerName})`;
          default: return paymentMethod;
        }
      };

      const receipt = {
        invoiceId: savedInvoice.id,
        invoiceNumber: savedInvoice.invoice_number,
        receiptNumber: transactionId,
        date: new Date().toLocaleString(),
        items: cart.map(item => {
          const itemColor = colors.find(c => c.id === item.color_id);
          const itemSize = sizes.find(s => s.id === item.size_id);
          return {
            name: item.name,
            reference_code: item.reference_code,
            category: item.category,
            color: itemColor?.name,
            size: itemSize?.name,
            quantity: item.cartQuantity,
            unit_price: item.sell_price!,
            total: item.lineTotal
          };
        }),
        subtotal,
        tax: taxAmount,
        total,
        paymentMethod: getPaymentMethodLabel(),
        paymentStatus: savedInvoice.payment_status,
        customerName: savedInvoice.customer_name,
        companyInfo: {
          name: user?.company_name,
          address: user?.company_address,
          phone: user?.company_phone,
          email: user?.company_email,
          tax_id: user?.company_tax_id,
          website: user?.company_website,
          logo_url: user?.logo_url
        }
      };

      setReceiptData(receipt);
      setShowReceipt(true);
      await loadAllProductLocationQtys();

    } catch (error) {
      console.error('Pickup processing error:', error);
      alert(t('payment.processingFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const processOldInvoice = async () => {
    if (cart.length === 0) {
      alert(t('payment.cartEmpty'));
      return;
    }

    if (!selectedInvoiceBuyer) {
      alert('Please select a B2B buyer');
      return;
    }

    if (!adminId) {
      alert('Admin account required');
      return;
    }

    setProcessing(true);

    try {
      const lines = invoiceCart;
      const { subtotal, taxAmount, total } = calculateInvoiceTotals();

      const items = lines.map(cartItem => {
        const itemColor = colors.find(c => c.id === cartItem.color_id);
        const itemSize = sizes.find(s => s.id === cartItem.size_id);
        const unitPrice = (cartItem.sell_price || 0);
        return {
          name: cartItem.name,
          sku: cartItem.sku,
          color_name: itemColor?.name,
          size_name: itemSize?.name,
          quantity: cartItem.cartQuantity,
          unit_price: unitPrice,
          total_price: cartItem.lineTotal,
          product_id: cartItem.id,
        };
      });

      const result = await createManualProFormaInvoice({
        adminId,
        buyer: selectedInvoiceBuyer,
        items,
        subtotal,
        taxAmount,
        totalAmount: total,
        vatPercentage: invoiceVatPct,
        invoiceDate: new Date(invoiceDate),
      });

      if (!result.success || !result.invoice) {
        throw new Error(result.error || 'Failed to create pro-forma invoice');
      }

      if (removeStockOnInvoice) {
        for (const cartItem of cart) {
          const whQty = getWarehouseQuantity(cartItem.id);
          const newWhQty = Math.max(0, whQty - cartItem.cartQuantity);
          const newTotalQty = Math.max(0, cartItem.quantity - cartItem.cartQuantity);

          const success = await updateItem(cartItem.id, {
            quantity: newTotalQty
          });

          if (!success) {
            throw new Error(`Failed to update inventory for ${cartItem.name}`);
          }

          if (warehouseLocation && adminId) {
            const locQtys = productLocationQtys[cartItem.id] || [];
            const updatedQtys = locQtys.map(lq => ({
              locationId: lq.locationId,
              quantity: lq.locationId === warehouseLocation.id ? newWhQty : lq.quantity
            }));
            await saveLocationQuantities(cartItem.id, adminId, updatedQtys);
          }
        }

        await loadAllProductLocationQtys();
      }

      if (sendInvoiceEmailFlag && selectedInvoiceBuyer.email?.trim()) {
        try {
          const supplier: SupplierProfile = {
            company_name: user?.company_name,
            legal_entity_name: user?.legal_entity_name,
            company_street: user?.company_street,
            company_house_number: user?.company_house_number,
            company_postal_code: user?.company_postal_code,
            company_city: user?.company_city,
            company_country: user?.company_country,
            company_address: user?.company_address,
            company_phone: user?.company_phone,
            company_email: user?.company_email,
            company_website: user?.company_website,
            kvk_number: user?.kvk_number,
            vat_number: user?.vat_number,
            company_tax_id: user?.company_tax_id,
            iban: user?.iban,
          };

          const epcQr = await buildEpcQrDataUrl(
            supplier,
            result.invoice.total_amount,
            result.invoice.invoice_number
          );
          const invoiceHtml = generateProFormaInvoiceHTML(
            result.invoice,
            selectedInvoiceBuyer,
            supplier,
            epcQr
          );
          const invPdf = await htmlToPdfBase64(invoiceHtml, { scale: 1.5, jpegQuality: 0.85 });

          await sendEdgeEmail({
            to: selectedInvoiceBuyer.email.trim(),
            subject: `Pro-forma invoice ${result.invoice.invoice_number}`,
            html: `<p>Please find your pro-forma invoice attached.</p>`,
            attachments: [{
              filename: `Invoice-${result.invoice.invoice_number}.pdf`,
              content: invPdf,
              contentType: 'application/pdf',
              encoding: 'base64',
            }],
            adminId,
          });
        } catch (emailError) {
          console.warn('Failed to send pro-forma email:', emailError);
        }
      }

      setCart([]);
      setSelectedInvoiceBuyer(null);
      setRemoveStockOnInvoice(false);
      setSendInvoiceEmailFlag(false);
      alert(`Pro-forma invoice ${result.invoice.invoice_number} created successfully.`);
    } catch (error) {
      console.error('Old invoice processing error:', error);
      alert(error instanceof Error ? error.message : 'Failed to create invoice');
    } finally {
      setProcessing(false);
    }
  };

  const handleReservationPickup = async (reservation: Reservation, pickupPaymentMethod: PaymentMethod, pickupWorkerName?: string, pickupEmail?: string) => {
    setProcessing(true);
    try {
      const fulfillmentResult = await fulfillReservation(reservation.id);
      if (fulfillmentResult.success) {
        let invoiceSource = reservation;
        if (fulfillmentResult.adjustments.length > 0) {
          const lines = fulfillmentResult.adjustments.map(a => {
            const name = a.productName || allItems.find(p => p.id === a.productId)?.name || 'Item';
            if (a.reason === 'unavailable') {
              return `\u2022 ${name}: reserved ${a.requested}, none in stock \u2014 not fulfilled.`;
            }
            return `\u2022 ${name}: reserved ${a.requested}, only ${a.fulfilled} in stock \u2014 fulfilled ${a.fulfilled}.`;
          }).join('\n');
          alert(
            `Reservation fulfilled, but stock changed since it was created:\n\n${lines}\n\n` +
            'The reservation and invoice will reflect what was actually fulfilled.'
          );

          // Re-read the reservation so the invoice reflects the post-adjustment
          // quantities and totals rather than the stale pre-fulfillment values.
          const { data: refreshed } = await supabase
            .from('reservations')
            .select('*, reservation_items(*)')
            .eq('id', reservation.id)
            .single();
          if (refreshed) invoiceSource = refreshed as Reservation;
        }
        const paymentStatus = pickupPaymentMethod === 'loan' ? 'unpaid' : pickupPaymentMethod === 'worker_pickup' ? 'internal' : 'paid';

        const invoiceData = {
          user_id: user?.id || '',
          invoice_number: `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          invoice_date: new Date().toISOString(),
          due_date: new Date(Date.now() + (user?.payment_terms_days || 14) * 24 * 60 * 60 * 1000).toISOString(),
          customer_name: reservation.customer_name,
          customer_address: reservation.customer_address || '',
          customer_vat_number: '',
          customer_email: pickupEmail || reservation.customer_email || '',
          subtotal_amount: invoiceSource.subtotal || 0,
          tax_amount: invoiceSource.tax_amount || 0,
          total_amount: invoiceSource.total_amount || 0,
          payment_method: (pickupPaymentMethod === 'worker_pickup' ? 'cash' : pickupPaymentMethod === 'loan' ? 'lowan' : pickupPaymentMethod) as 'cash' | 'pin' | 'bank_transfer' | 'lowan',
          payment_status: paymentStatus as 'paid' | 'unpaid',
          currency: 'EUR',
          language: language as 'en' | 'nl',
          notes: `Reservation ${reservation.reservation_number} pickup`,
          invoice_items: (invoiceSource.reservation_items || [])
            .filter(item => item.quantity > 0)
            .map(item => {
              const product = allItems.find(p => p.id === item.product_id);
              return {
                id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                invoice_id: '',
                inventory_item_id: item.product_id,
                description: product?.name || 'Product',
                quantity: item.quantity,
                unit_price: item.unit_price || 0,
                total_price: item.total_price || 0,
                vat_rate: effectiveTaxRate,
                vat_amount: (item.total_price || 0) * (effectiveTaxRate / 100),
                created_at: new Date().toISOString()
              };
            }) as any
        };

        const savedInvoice = await createInvoice(invoiceData);

        if (savedInvoice && pickupEmail?.trim()) {
          try {
            await sendInvoiceEmail(savedInvoice, user!, pickupEmail.trim());
          } catch (emailError) {
            console.warn('Failed to send invoice email:', emailError);
          }
        }

        await createTransaction({
          customer_name: reservation.customer_name,
          payment_method: pickupPaymentMethod,
          payment_status: paymentStatus,
          worker_name: pickupWorkerName,
          subtotal: invoiceSource.subtotal,
          tax_amount: invoiceSource.tax_amount,
          total_amount: invoiceSource.total_amount,
          items_count: (invoiceSource.reservation_items || []).filter(it => it.quantity > 0).length,
          reservation_id: reservation.id,
          invoice_id: savedInvoice?.id,
          notes: `Reservation ${reservation.reservation_number} fulfilled`
        });

        invalidateAllItemsCache();
        await fetchAllItems(true);
        await loadAllProductLocationQtys();
        setShowPickupModal(false);
        alert(`Reservation ${reservation.reservation_number} fulfilled successfully!${savedInvoice ? ` Invoice ${savedInvoice.invoice_number} created.` : ''}${pickupEmail ? ' Email sent.' : ''}`);
      } else {
        alert('Failed to fulfill reservation');
      }
    } catch (error) {
      console.error('Error fulfilling reservation:', error);
      alert('Failed to fulfill reservation');
    } finally {
      setProcessing(false);
    }
  };

  const activeReservations = getActiveReservations();

  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    allItems.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [allItems]);

  const baseVariants = useMemo(() => {
    return allItems.filter(item => {
      if (shouldShowPricing && !item.sell_price) return false;

      if (!isOldInvoice) {
        const locationQty = getLocationQuantity(item.id);

        if (showOutOfStock) {
          if (!hasLocationRecord(item.id)) return false;
        } else if (locationQty <= 0) {
          return false;
        }

        if (shipmentMode === 'transfer' && transferSourceLocation) {
          if (!showOutOfStock && locationQty <= 0) return false;
          if (showOutOfStock && !hasLocationRecord(item.id)) return false;
        }
      }

      if (filterCategory && item.category !== filterCategory) return false;
      if (filterColor && item.color_id !== filterColor) return false;
      if (filterSize && item.size_id !== filterSize) return false;

      return true;
    });
  }, [
    allItems,
    shouldShowPricing,
    isOldInvoice,
    showOutOfStock,
    filterCategory,
    filterColor,
    filterSize,
    shipmentMode,
    transferSourceLocation,
    getLocationQuantity,
    hasLocationRecord
  ]);

  const productGroups = useMemo(
    () => buildProductNameGroups(baseVariants, colors, sizes, getLocationQuantity),
    [baseVariants, colors, sizes, getLocationQuantity]
  );

  const filteredGroups = useMemo(
    () => filterProductGroups(productGroups, searchQuery, colors),
    [productGroups, searchQuery, colors]
  );

  const filteredVariants = useMemo(() => {
    if (!searchQuery.trim()) return baseVariants;
    const q = searchQuery.toLowerCase().trim();
    const exactRef = baseVariants.filter(i => i.reference_code?.toLowerCase() === q);
    if (exactRef.length) return exactRef;
    const exactBarcode = baseVariants.filter(i => i.barcode?.toLowerCase() === q);
    if (exactBarcode.length) return exactBarcode;
    return baseVariants.filter(i =>
      i.name.toLowerCase().includes(q) ||
      i.sku?.toLowerCase().includes(q) ||
      i.reference_code?.toLowerCase().includes(q)
    );
  }, [baseVariants, searchQuery]);

  const browseItemCount = browseView === 'all' ? filteredVariants.length : filteredGroups.length;

  const hasActiveFilters = !!(searchQuery || filterCategory || filterColor || filterSize);

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterCategory('');
    setFilterColor('');
    setFilterSize('');
  };

  const getMatrixAvailableQty = useCallback((productId: string): number => {
    const reserved = getReservedQuantityForProduct(productId);
    const locationQty = getLocationQuantity(productId);
    const inCart = getCartQuantityForProduct(productId);
    return Math.max(0, locationQty - reserved - inCart);
  }, [getLocationQuantity, getReservedQuantityForProduct, getCartQuantityForProduct]);

  const handleSelectProductGroup = (group: ProductNameGroup) => {
    setSelectedProductGroup(group);
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] p-4 sm:p-6 gap-4">
      <header className="flex-shrink-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
          In-house Shipment
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Reserve items, process pickups, or transfer stock between locations
        </p>
      </header>

      <div className="flex flex-col xl:flex-row gap-4 xl:gap-6 flex-1 min-h-0">
        <section className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="card flex flex-col flex-1 min-h-0 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 flex-shrink-0 gap-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('inhouse.selectProducts')}
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                  {browseItemCount} {browseView === 'all' ? 'variants' : 'products'}
                </span>
                {browseView === 'grouped' && (
                  <SegmentedControl<ProductGroupDensity>
                    value={browseDensity}
                    onChange={setBrowseDensity}
                    ariaLabel="View density"
                    options={[
                      { value: 'list', label: '', icon: <List className="h-4 w-4" /> },
                      { value: 'grid', label: '', icon: <LayoutGrid className="h-4 w-4" /> }
                    ]}
                  />
                )}
              </div>
            </div>

            <div className="sticky top-0 z-20 bg-white dark:bg-gray-800 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-1 pb-3 border-b border-gray-100 dark:border-gray-700/50 flex-shrink-0">
              <InhouseCommandToolbar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filterCategory={filterCategory}
                onFilterCategoryChange={setFilterCategory}
                filterColor={filterColor}
                onFilterColorChange={setFilterColor}
                filterSize={filterSize}
                onFilterSizeChange={setFilterSize}
                categories={allCategories}
                colors={colors}
                sizes={sizes}
                showOutOfStock={showOutOfStock}
                onShowOutOfStockChange={setShowOutOfStock}
                onOpenCamera={() => setShowCameraScanner(true)}
                onOpenExternalScanner={() => setShowExternalScanner(true)}
                onClearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
                browseView={browseView}
                onBrowseViewChange={setBrowseView}
              />
            </div>

            <div className="flex-1 overflow-y-auto mt-4 min-h-[320px] max-h-[calc(100vh-280px)] pr-1">
              {allItemsLoading ? (
                browseView === 'all' || browseDensity === 'grid' ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <ProductGroupCardSkeleton key={i} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <ProductGroupListSkeleton key={i} />
                    ))}
                  </div>
                )
              ) : browseView === 'all' ? (
                filteredVariants.length === 0 ? (
                  <EmptyState
                    icon={Package}
                    title={hasActiveFilters ? 'No matching products' : 'No products available'}
                    description={
                      hasActiveFilters
                        ? 'Try adjusting your search or filters'
                        : showOutOfStock
                          ? 'No products assigned to this location'
                          : 'Add products with stock to start'
                    }
                    action={
                      hasActiveFilters ? (
                        <button
                          type="button"
                          onClick={handleClearFilters}
                          className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                        >
                          Clear all filters
                        </button>
                      ) : undefined
                    }
                  />
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {filteredVariants.map(item => {
                      const locationQty = getLocationQuantity(item.id);
                      const reservedQty = getReservedQuantityForProduct(item.id);
                      const availableQty = getAvailableQuantity(item);

                      return (
                        <ProductVariantCard
                          key={item.id}
                          item={item}
                          colors={colors}
                          sizes={sizes}
                          shouldShowPricing={shouldShowPricing}
                          shipmentMode={shipmentMode}
                          locationQty={locationQty}
                          reservedQty={reservedQty}
                          availableQty={availableQty}
                          onAdd={() => addToCart(item, 1)}
                        />
                      );
                    })}
                  </div>
                )
              ) : filteredGroups.length === 0 ? (
                <EmptyState
                  icon={Package}
                  title={hasActiveFilters ? 'No matching products' : 'No products available'}
                  description={
                    hasActiveFilters
                      ? 'Try adjusting your search or filters'
                      : showOutOfStock
                        ? 'No products assigned to this location'
                        : 'Add products with stock to start'
                  }
                  action={
                    hasActiveFilters ? (
                      <button
                        type="button"
                        onClick={handleClearFilters}
                        className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                      >
                        Clear all filters
                      </button>
                    ) : undefined
                  }
                />
              ) : browseDensity === 'list' ? (
                <div className="flex flex-col gap-2">
                  {filteredGroups.map(group => (
                    <ProductGroupCard
                      key={group.productName}
                      group={group}
                      density="list"
                      showPricing={shouldShowPricing}
                      colorSwatches={colors}
                      onSelect={handleSelectProductGroup}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {filteredGroups.map(group => (
                    <ProductGroupCard
                      key={group.productName}
                      group={group}
                      density="grid"
                      showPricing={shouldShowPricing}
                      colorSwatches={colors}
                      onSelect={handleSelectProductGroup}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className="w-full xl:w-[380px] xl:max-w-[420px] flex-shrink-0 xl:sticky xl:top-4 xl:self-start xl:max-h-[calc(100vh-2rem)] flex flex-col">
          <InhouseOrderPanel
            t={t}
            shipmentMode={shipmentMode}
            onShipmentModeChange={setShipmentMode}
            cart={isOldInvoice ? invoiceCart : cart}
            onClearCart={clearCart}
            colors={colors}
            sizes={sizes}
            allItems={allItems}
            getAvailableQuantity={getAvailableQuantity}
            shouldShowPricing={shouldShowPricing}
            warehouseLocationName={warehouseLocation?.name}
            shopLocationName={shopLocation?.name}
            editingPriceId={editingPriceId}
            editingPriceValue={editingPriceValue}
            onRemoveFromCart={removeFromCart}
            onUpdateCartQuantity={updateCartQuantity}
            onPriceEditStart={handlePriceEdit}
            onPriceEditSave={savePriceEdit}
            onPriceEditCancel={cancelPriceEdit}
            onPriceEditChange={setEditingPriceValue}
            onResetCartItemPrice={resetCartItemPrice}
            subtotal={subtotal}
            taxAmount={taxAmount}
            total={total}
            effectiveTaxRate={isOldInvoice ? invoiceVatPct : effectiveTaxRate}
            customTaxRate={customTaxRate}
            isEditingTax={isEditingTax}
            editingTaxValue={editingTaxValue}
            onTaxEditStart={handleTaxEdit}
            onTaxEditSave={saveTaxEdit}
            onTaxEditCancel={() => setIsEditingTax(false)}
            onTaxEditChange={setEditingTaxValue}
            onResetTaxRate={resetTaxRate}
            paymentMethod={paymentMethod}
            onPaymentMethodChange={setPaymentMethod}
            workerName={workerName}
            onWorkerNameChange={setWorkerName}
            selectedWorker={selectedWorker}
            onSelectedWorkerChange={setSelectedWorker}
            transferSourceLocation={transferSourceLocation}
            transferDestLocation={transferDestLocation}
            onTransferSourceChange={setTransferSourceLocation}
            onTransferDestChange={setTransferDestLocation}
            transferNotes={transferNotes}
            onTransferNotesChange={setTransferNotes}
            availableLocations={availableLocations}
            customerData={customerData}
            onCustomerDataChange={setCustomerData}
            showCustomerForm={showCustomerForm}
            onToggleCustomerForm={() => setShowCustomerForm(!showCustomerForm)}
            processing={processing}
            onHoldCart={handleHoldCart}
            onPrimaryAction={
              shipmentMode === 'reserve' ? processReservation :
              shipmentMode === 'transfer' ? processTransfer :
              shipmentMode === 'old_invoice' ? processOldInvoice :
              processPickup
            }
            activeReservations={activeReservations}
            pendingTransferCount={getPendingTransfers().length}
            transferHistory={getInternalTransfers()}
            showTransferHistory={showTransferHistory}
            onToggleTransferHistory={() => setShowTransferHistory(!showTransferHistory)}
            onOpenReservations={() => setShowPickupModal(true)}
            onOpenReceiveTransfer={() => setShowTransferReceiptModal(true)}
            onOpenReturnStock={() => setShowReturnStockModal(true)}
            heldCarts={heldCarts}
            onRestoreCart={handleRestoreCart}
            onDeleteHeldCart={deleteHeldCart}
            isOldInvoice={isOldInvoice}
            buyers={buyers}
            selectedInvoiceBuyer={selectedInvoiceBuyer}
            onSelectedInvoiceBuyerChange={setSelectedInvoiceBuyer}
            invoiceDate={invoiceDate}
            onInvoiceDateChange={setInvoiceDate}
            removeStockOnInvoice={removeStockOnInvoice}
            onToggleRemoveStock={() => setRemoveStockOnInvoice(prev => !prev)}
            sendInvoiceEmailFlag={sendInvoiceEmailFlag}
            onToggleSendInvoiceEmail={() => setSendInvoiceEmailFlag(prev => !prev)}
            invoiceVatPct={invoiceVatPct}
            allowOverStock={ignoreStock}
          />
        </aside>
      </div>

      {selectedProductGroup && (
        <ProductVariantMatrixModal
          group={selectedProductGroup}
          colors={colors}
          sizes={sizes}
          showPricing={shouldShowPricing}
          showOutOfStock={showOutOfStock}
          onClose={() => setSelectedProductGroup(null)}
          onAddVariants={addVariantsToCart}
          getLocationQty={getLocationQuantity}
          getAvailableQty={getMatrixAvailableQty}
          getReservedQty={getReservedQuantityForProduct}
          ignoreStock={ignoreStock}
        />
      )}

      <BarcodeScanner
        isOpen={showCameraScanner}
        onClose={() => setShowCameraScanner(false)}
        onScan={(code) => void handleProductScan(code)}
        title="Scan Product Barcode"
      />

      <ExternalScanner
        isOpen={showExternalScanner}
        onClose={() => setShowExternalScanner(false)}
        onScan={(code, item) => void handleProductScan(code, item)}
        mode="payment"
        title="Scan Product - External Device"
      />

      {showReceipt && receiptData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Pickup Successful
                </h2>
                <button
                  onClick={() => {
                    setShowReceipt(false);
                    clearCart();
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <Receipt
                receiptNumber={receiptData.receiptNumber}
                date={receiptData.date}
                items={receiptData.items}
                subtotal={receiptData.subtotal}
                tax={receiptData.tax}
                total={receiptData.total}
                paymentMethod={receiptData.paymentMethod}
                companyInfo={receiptData.companyInfo}
              />

              <div className="flex gap-4 mt-6">
                <Button
                  onClick={() => window.print()}
                  variant="primary"
                  className="flex-1"
                >
                  <ReceiptIcon className="h-4 w-4 mr-2" />
                  Print Receipt
                </Button>
                <Button
                  onClick={() => {
                    setShowReceipt(false);
                    clearCart();
                  }}
                  variant="secondary"
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <EnhancedPickupModal
        isOpen={showPickupModal}
        onClose={() => setShowPickupModal(false)}
        reservations={activeReservations}
        onPickup={handleReservationPickup}
        processing={processing}
        products={allItems}
        colors={colors}
        sizes={sizes}
      />

      <ReservationConfirmation
        isOpen={showReservationConfirmation}
        onClose={() => setShowReservationConfirmation(false)}
        reservation={lastCreatedReservation}
        items={allItems}
        colors={colors}
        sizes={sizes}
        createdByName={user?.full_name || user?.email || 'Unknown'}
      />

      <TransferConfirmationModal
        isOpen={showTransferConfirmation}
        onClose={() => setShowTransferConfirmation(false)}
        transfer={lastCreatedTransfer}
        items={allItems}
        colors={colors}
        sizes={sizes}
        locations={availableLocations as LocationInfo[]}
        createdByName={user?.full_name || user?.email || 'Unknown'}
      />

      <TransferReceiptModal
        isOpen={showTransferReceiptModal}
        onClose={() => setShowTransferReceiptModal(false)}
        pendingTransfers={getPendingTransfers()}
        onConfirmReceipt={handleConfirmTransferReceipt}
        processing={processing}
        products={allItems}
        colors={colors}
        sizes={sizes}
        locations={availableLocations as LocationInfo[]}
      />

      <ReturnStockModal
        isOpen={showReturnStockModal}
        onClose={() => setShowReturnStockModal(false)}
        onCreateReturn={handleCreateReturn}
        processing={processing}
        products={allItems}
        colors={colors}
        sizes={sizes}
        locations={availableLocations as LocationInfo[]}
        adminId={adminId || ''}
      />

      <div className="fixed top-4 right-4 z-[60] space-y-2 pointer-events-none">
        {scanNotifications.map((notification) => (
          <div
            key={notification.id}
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg transform transition-all duration-300 animate-slide-in min-w-[280px] max-w-[360px] ${
              notification.type === 'success'
                ? 'bg-green-600 text-white'
                : notification.type === 'warning'
                ? 'bg-amber-500 text-white'
                : 'bg-red-600 text-white'
            }`}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              notification.type === 'success'
                ? 'bg-green-500'
                : notification.type === 'warning'
                ? 'bg-amber-400'
                : 'bg-red-500'
            }`}>
              {notification.type === 'success' ? (
                <CheckCircle className="h-6 w-6" />
              ) : notification.type === 'warning' ? (
                <AlertTriangle className="h-6 w-6" />
              ) : (
                <X className="h-6 w-6" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">
                {notification.itemName}
              </p>
              {notification.type === 'success' ? (
                <p className="text-xs opacity-90">
                  {notification.price !== undefined && `${'\u20AC'}${notification.price.toFixed(2)}`}
                  {notification.quantity !== undefined && ` - Qty: ${notification.quantity}`}
                </p>
              ) : (
                <p className="text-xs opacity-90">{notification.message}</p>
              )}
            </div>
            <button
              onClick={() => removeScanNotification(notification.id)}
              className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};
